import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Grid,
  Snackbar
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';

// Utility function to format dates properly without timezone issues
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  // Use a UTC time approach with fixed noon time to avoid timezone issues
  return format(new Date(`${dateString.split('T')[0]}T12:00:00Z`), 'dd/MM/yyyy');
};

const MyNewConverts = () => {
  const [newConverts, setNewConverts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFollowUpDialog, setShowFollowUpDialog] = useState(false);
  const [selectedConvert, setSelectedConvert] = useState(null);
  const [followUpType, setFollowUpType] = useState('phone_call');
  const [contactedBy, setContactedBy] = useState('');
  const [status, setStatus] = useState('contacted');
  const [followUpDate, setFollowUpDate] = useState(new Date());
  const [recommendation, setRecommendation] = useState('');
  const [notes, setNotes] = useState('');
  const [foundationSchool, setFoundationSchool] = useState(false);
  const [joinedWSF, setJoinedWSF] = useState(false);
  const [joinedServiceUnit, setJoinedServiceUnit] = useState(false);
  const [waterBaptism, setWaterBaptism] = useState(false);
  const [holyGhostBaptism, setHolyGhostBaptism] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [page]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/new-convert-assignments/my-new-converts?page=${page}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setNewConverts(response.data.data);
      setTotalPages(response.data.totalPages);
      setLoading(false);
    } catch (err) {
      setError('Failed to load assigned new converts.');
      setLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleOpenFollowUpDialog = (convert) => {
    setSelectedConvert(convert);
    setFollowUpType('phone_call');
    setContactedBy(`${user?.first_name} ${user?.last_name}`);
    setStatus('contacted');
    setRecommendation('');
    setNotes('');
    setFollowUpDate(new Date());
    setFoundationSchool(false);
    setJoinedWSF(false);
    setJoinedServiceUnit(false);
    setWaterBaptism(false);
    setHolyGhostBaptism(false);
    setShowFollowUpDialog(true);
  };

  const handleCloseFollowUpDialog = () => {
    setShowFollowUpDialog(false);
    setSelectedConvert(null);
  };

  const handleFollowUpSubmit = async () => {
    if (!selectedConvert) return;
    try {
      const url = `${process.env.REACT_APP_API_URL}/api/new-convert-follow-up/${selectedConvert.id}`;
      const followUpData = {
        notes,
        status,
        follow_up_type: followUpType,
        contacted_by: contactedBy,
        recommendation,
        follow_up_date: followUpDate,
        completed_foundation_school: foundationSchool,
        joined_wsf: joinedWSF,
        joined_service_unit: joinedServiceUnit,
        done_water_baptism: waterBaptism,
        received_holy_ghost_baptism: holyGhostBaptism
      };
      await axios.post(url, followUpData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setSnackbar({ open: true, message: 'Follow-up saved successfully', severity: 'success' });
      handleCloseFollowUpDialog();
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save follow-up', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          My New Converts
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Follow-up</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {newConverts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No assigned new converts found.
                  </TableCell>
                </TableRow>
              ) : (
                newConverts.map((convert) => (
                  <TableRow key={convert.id}>
                    <TableCell>
                      {convert.first_name} {convert.last_name}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{convert.phone_number || 'No phone'}</Typography>
                      <Typography variant="body2" color="textSecondary">{convert.email || 'No email'}</Typography>
                    </TableCell>
                    <TableCell>
                      {convert.date ? format(new Date(convert.date), 'dd/MM/yyyy') : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {convert.follow_up_status ? convert.follow_up_status.charAt(0).toUpperCase() + convert.follow_up_status.slice(1) : 'Pending'}
                    </TableCell>
                    <TableCell>
                      {convert.last_follow_up_date ? format(new Date(convert.last_follow_up_date), 'dd/MM/yyyy') : '—'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleOpenFollowUpDialog(convert)}
                      >
                        Add Follow-Up
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={handlePageChange} 
            color="primary" 
          />
        </Box>
      </Paper>
      <Dialog open={showFollowUpDialog} onClose={handleCloseFollowUpDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Follow-up</DialogTitle>
        <DialogContent>
          {selectedConvert && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">
                New Convert: {selectedConvert.first_name} {selectedConvert.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Date: {selectedConvert.date ? format(new Date(selectedConvert.date), 'dd/MM/yyyy') : 'N/A'}
              </Typography>
            </Box>
          )}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Follow-up Date"
              value={followUpDate}
              onChange={(newValue) => setFollowUpDate(newValue)}
              maxDate={new Date()}
              renderInput={(params) => <TextField {...params} fullWidth margin="normal" required />}
            />
          </LocalizationProvider>
          <FormControl component="fieldset" fullWidth sx={{ mt: 2 }}>
            <FormLabel component="legend">Follow-up Type</FormLabel>
            <RadioGroup
              row
              name="follow-up-type"
              value={followUpType}
              onChange={(e) => setFollowUpType(e.target.value)}
            >
              <FormControlLabel value="phone_call" control={<Radio />} label="Phone Call" />
              <FormControlLabel value="email" control={<Radio />} label="Email" />
              <FormControlLabel value="home_visit" control={<Radio />} label="Home Visit" />
              <FormControlLabel value="homecell" control={<Radio />} label="Homecell" />
              <FormControlLabel value="other" control={<Radio />} label="Other" />
            </RadioGroup>
          </FormControl>
          <TextField
            fullWidth
            label="Contacted By"
            value={contactedBy}
            onChange={(e) => setContactedBy(e.target.value)}
            required
            sx={{ mt: 2 }}
          />
          <FormControl component="fieldset" fullWidth sx={{ mt: 2 }}>
            <FormLabel component="legend">Status</FormLabel>
            <RadioGroup
              row
              name="status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <FormControlLabel value="contacted" control={<Radio />} label="Contacted" />
              <FormControlLabel value="completed" control={<Radio />} label="Completed" />
              <FormControlLabel value="unreachable" control={<Radio />} label="Unreachable" />
              <FormControlLabel value="pending" control={<Radio />} label="Pending" />
            </RadioGroup>
          </FormControl>
          <TextField
            select
            fullWidth
            margin="normal"
            label="Recommendation"
            value={recommendation}
            onChange={(e) => setRecommendation(e.target.value)}
          >
            <MenuItem value="more_follow_up">More Follow-up</MenuItem>
            <MenuItem value="stronger_follow_up">Stronger Follow-up</MenuItem>
            <MenuItem value="established">Established</MenuItem>
            <MenuItem value="add_to_homecell">Add to Homecell</MenuItem>
            <MenuItem value="join_service_group">Join Service Group</MenuItem>
            <MenuItem value="non_permanent_visitor">Non-permanent Visitor</MenuItem>
            <MenuItem value="not_reachable">Not Reachable</MenuItem>
          </TextField>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Growth Status</Typography>
            <Grid container>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={foundationSchool} onChange={(e) => setFoundationSchool(e.target.checked)} />}
                  label="Completed Foundation School"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={joinedWSF} onChange={(e) => setJoinedWSF(e.target.checked)} />}
                  label="Joined WSF"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={joinedServiceUnit} onChange={(e) => setJoinedServiceUnit(e.target.checked)} />}
                  label="Joined Service Unit"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={waterBaptism} onChange={(e) => setWaterBaptism(e.target.checked)} />}
                  label="Done Water Baptism"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={holyGhostBaptism} onChange={(e) => setHolyGhostBaptism(e.target.checked)} />}
                  label="Received Holy Ghost Baptism"
                />
              </Grid>
            </Grid>
          </Box>
          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Enter any additional notes about the follow-up here..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFollowUpDialog}>Cancel</Button>
          <Button onClick={handleFollowUpSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MyNewConverts; 